import React, { useRef, useEffect, useCallback, useState } from "react";
import AnnotationToolbar, { AnnotationTool } from "./AnnotationToolbar";
import { usePdfViewer } from "../hooks/usePdfViewer";
import { useAnnotation } from "../hooks/useAnnotation";
import DvsEvaluationService, {
  AnnotationPoint,
} from "../../../_services/DvsEvaluationService";

interface Props {
  mappingId: number;
  testPaperUrl: string;
  employeeId: number;
}

const PdfAnnotationViewer: React.FC<Props> = ({
  mappingId,
  testPaperUrl,
  employeeId,
}) => {
  const {
    pdfCanvasRef,
    totalPages,
    currentPage,
    scale,
    rotation,
    rendering,
    loadDocument,
    renderPage,
    goToPage,
    setScale,
    setRotation,
    setScaleState,
    setRotationState,
    cancelRender,
  } = usePdfViewer();

  const {
    canUndo,
    canRedo,
    loadPage,
    addStroke,
    undo,
    redo,
    clearPage,
    getAllPoints,
  } = useAnnotation();

  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const currentStroke = useRef<AnnotationPoint[]>([]);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const endPoint = useRef<{ x: number; y: number } | null>(null);

  const [brushColor, setBrushColor] = useState("#FF0000");
  const [lineWidth, setLineWidth] = useState(2);
  const [canvasDims, setCanvasDims] = useState({ width: 0, height: 0 });
  const [flipped, setFlipped] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [activeTool, setActiveTool] = useState<AnnotationTool>("pen");
  const [editingTextBox, setEditingTextBox] = useState<string | null>(null);
  const [editingTextContent, setEditingTextContent] = useState<string>("");
  const [hoverTextBoxId, setHoverTextBoxId] = useState<string | null>(null);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);

  // Refs for values needed by native touch listeners (always current)
  const brushColorRef = useRef(brushColor);
  brushColorRef.current = brushColor;
  const lineWidthRef = useRef(lineWidth);
  lineWidthRef.current = lineWidth;
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const addStrokeRef = useRef(addStroke);
  addStrokeRef.current = addStroke;

  // ── Imperative canvas helpers ──────────────────────────────────────

  /** Set annotation canvas buffer size. Setting canvas.width/height clears it. */
  const setAnnotationCanvasDims = useCallback(
    (dims: { width: number; height: number }) => {
      const canvas = annotationCanvasRef.current;
      if (canvas && dims.width > 0) {
        canvas.width = dims.width;
        canvas.height = dims.height;
      }
      setCanvasDims(dims); // for CSS sizing only
    },
    [],
  );

  // Get text boxes from annotations
  const getTextBoxesFromAnnotations = useCallback(() => {
    const allPoints = getAllPoints();
    return allPoints
      .filter((p) => p.type === "text")
      .map((p) => ({
        id: p.id || `text_${Math.random()}`,
        x: p.x,
        y: p.y,
        width: p.width || 200,
        height: p.height || 60,
        text: p.text || "",
        color: p.ss,
        fontSize: p.fontSize || 16,
      }));
  }, [getAllPoints]);

  /** Draw annotation points directly on the annotation canvas — fully imperative. */
  const drawPointsOnCanvas = useCallback(
    (points: AnnotationPoint[], drawScale: number) => {
      const ctx = annotationCanvasRef.current?.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

      for (const p of points) {
        const ratio = p.px ? drawScale / p.px : 1;
        const type = p.type || "stroke";

        if (type === "stroke") {
          // Regular pen stroke
          ctx.beginPath();
          ctx.moveTo(p.x * ratio, p.y * ratio);
          ctx.lineTo(p.ex * ratio, p.ey * ratio);
          ctx.strokeStyle = p.ss;
          ctx.lineWidth = p.lw;
          ctx.lineCap = "round";
          ctx.stroke();
        } else if (type === "highlighter") {
          // Highlighter stroke with transparency and blur
          ctx.beginPath();
          ctx.moveTo(p.x * ratio, p.y * ratio);
          ctx.lineTo(p.ex * ratio, p.ey * ratio);
          ctx.strokeStyle = p.ss;
          ctx.lineWidth = p.lw * 4;
          ctx.lineCap = "round";
          ctx.globalAlpha = 0.15;
          ctx.filter = "blur(2px)";
          ctx.stroke();
          ctx.globalAlpha = 1.0;
          ctx.filter = "none";
        } else if (type === "text") {
          // Text boxes are rendered as DOM elements, not on canvas
          // Skip rendering here
        } else if (type === "arrow") {
          // Arrow
          const headlen = 15 * ratio;
          const dx = (p.ex - p.x) * ratio;
          const dy = (p.ey - p.y) * ratio;
          const angle = Math.atan2(dy, dx);

          ctx.beginPath();
          ctx.moveTo(p.x * ratio, p.y * ratio);
          ctx.lineTo(p.ex * ratio, p.ey * ratio);
          ctx.strokeStyle = p.ss;
          ctx.lineWidth = p.lw;
          ctx.lineCap = "round";
          ctx.stroke();

          // Arrow head
          ctx.beginPath();
          ctx.moveTo(p.ex * ratio, p.ey * ratio);
          ctx.lineTo(
            p.ex * ratio - headlen * Math.cos(angle - Math.PI / 6),
            p.ey * ratio - headlen * Math.sin(angle - Math.PI / 6)
          );
          ctx.moveTo(p.ex * ratio, p.ey * ratio);
          ctx.lineTo(
            p.ex * ratio - headlen * Math.cos(angle + Math.PI / 6),
            p.ey * ratio - headlen * Math.sin(angle + Math.PI / 6)
          );
          ctx.strokeStyle = p.ss;
          ctx.lineWidth = p.lw;
          ctx.stroke();
        } else if (type === "circle") {
          // Circle
          const width = (p.width || (p.ex - p.x)) * ratio;
          const height = (p.height || (p.ey - p.y)) * ratio;
          const radiusX = Math.abs(width) / 2;
          const radiusY = Math.abs(height) / 2;
          const centerX = p.x * ratio + width / 2;
          const centerY = p.y * ratio + height / 2;

          ctx.beginPath();
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.strokeStyle = p.ss;
          ctx.lineWidth = p.lw;
          ctx.stroke();
        } else if (type === "rectangle") {
          // Rectangle
          const width = (p.width || (p.ex - p.x)) * ratio;
          const height = (p.height || (p.ey - p.y)) * ratio;

          ctx.beginPath();
          ctx.rect(p.x * ratio, p.y * ratio, width, height);
          ctx.strokeStyle = p.ss;
          ctx.lineWidth = p.lw;
          ctx.stroke();
        }
      }
    },
    [],
  );

  // ── Load PDF on mount ──────────────────────────────────────────────

  useEffect(() => {
    if (!testPaperUrl) return;
    let aborted = false;
    setPdfLoading(true);
    (async () => {
      console.log("[Mount] loading PDF...");
      const doc = await loadDocument(testPaperUrl);
      if (aborted || !doc) { setPdfLoading(false); return; }

      const dims = await renderPage(1, 1, 0, doc);
      if (aborted) return;
      setAnnotationCanvasDims(dims);
      const points = await loadPage(mappingId, 1);
      if (aborted) return;

      // Load saved paper position
      let drawScale = 1;
      try {
        const pos = await DvsEvaluationService.getPaperPosition(
          mappingId,
          1,
        );
        if (!aborted && (pos.angle !== 0 || pos.scale !== 1)) {
          const newDims = await renderPage(1, pos.scale, pos.angle, doc);
          if (aborted) return;
          setAnnotationCanvasDims(newDims);
          setScaleState(pos.scale);
          setRotationState(pos.angle);
          drawScale = pos.scale;
        }
      } catch {}

      if (aborted) return;
      // Draw annotations imperatively — no effects involved
      drawPointsOnCanvas(points, drawScale);
      setPdfLoading(false);
    })();
    return () => { aborted = true; cancelRender(); };
  }, [testPaperUrl, mappingId]);

  // Re-render PDF + annotations when tab regains focus (browsers may discard
  // canvas bitmaps for background tabs to save memory).
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible" && currentPage > 0) {
        renderPage(currentPage, scale, rotation).then((dims) => {
          if (dims.width > 0) {
            setAnnotationCanvasDims(dims);
            drawPointsOnCanvas(getAllPoints(), scale);
          }
        });
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [currentPage, scale, rotation, renderPage, getAllPoints, drawPointsOnCanvas, setAnnotationCanvasDims]);

  // ── Undo / Redo / Clear handlers ───────────────────────────────────

  const handleUndo = useCallback(async () => {
    const points = await undo();
    drawPointsOnCanvas(points, scale);
  }, [undo, drawPointsOnCanvas, scale]);

  const handleRedo = useCallback(async () => {
    const points = await redo();
    drawPointsOnCanvas(points, scale);
  }, [redo, drawPointsOnCanvas, scale]);

  const handleClear = useCallback(async () => {
    await clearPage();
    drawPointsOnCanvas([], scale);
  }, [clearPage, drawPointsOnCanvas, scale]);

  // ── Keyboard shortcuts ─────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "z") {
        e.preventDefault();
        handleUndo();
      } else if (e.ctrlKey && e.key === "y") {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleUndo, handleRedo]);

  // ── Drawing helpers (shared between mouse and touch) ───────────────

  const getPointFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = annotationCanvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const sx = canvas.width / rect.width;
      const sy = canvas.height / rect.height;
      let x = (clientX - rect.left) * sx;
      const y = (clientY - rect.top) * sy;
      // When flipped, mirror the x coordinate so annotations align with the visual position
      if (flipped) {
        x = canvas.width - x;
      }
      return { x, y };
    },
    [flipped],
  );

  const doStartDrawing = useCallback(
    (clientX: number, clientY: number) => {
      isDrawing.current = true;
      currentStroke.current = [];
      lastPoint.current = getPointFromEvent(clientX, clientY);
    },
    [getPointFromEvent],
  );

  const doDraw = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDrawing.current || !lastPoint.current) return;
      const pt = getPointFromEvent(clientX, clientY);
      const ctx = annotationCanvasRef.current?.getContext("2d");
      if (!ctx) return;

      // Store end point for shapes
      endPoint.current = pt;

      const tool = activeTool;

      if (tool === "pen" || tool === "highlighter") {
        // For pen and highlighter, draw continuous strokes
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.strokeStyle = brushColorRef.current;
        ctx.lineWidth = tool === "highlighter" ? lineWidthRef.current * 4 : lineWidthRef.current;
        ctx.lineCap = "round";
        if (tool === "highlighter") {
          ctx.globalAlpha = 0.15;
          ctx.filter = "blur(2px)";
        }
        ctx.stroke();
        ctx.globalAlpha = 1.0;
        ctx.filter = "none";

        const point: AnnotationPoint = {
          x: lastPoint.current.x,
          y: lastPoint.current.y,
          ss: brushColorRef.current,
          lw: lineWidthRef.current,
          ex: pt.x,
          ey: pt.y,
          px: scaleRef.current,
          type: tool === "highlighter" ? "highlighter" : "stroke",
        };
        currentStroke.current.push(point);
        lastPoint.current = pt;
      } else if (tool === "arrow" || tool === "circle" || tool === "rectangle") {
        // For shapes, clear and redraw preview during drag
        const allPoints = getAllPoints();
        drawPointsOnCanvas(allPoints, scaleRef.current);

        // Draw preview shape
        ctx.strokeStyle = brushColorRef.current;
        ctx.lineWidth = lineWidthRef.current;

        if (tool === "arrow") {
          // Arrow preview
          const headlen = 15;
          const dx = pt.x - lastPoint.current.x;
          const dy = pt.y - lastPoint.current.y;
          const angle = Math.atan2(dy, dx);

          ctx.beginPath();
          ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
          ctx.lineTo(pt.x, pt.y);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(pt.x, pt.y);
          ctx.lineTo(pt.x - headlen * Math.cos(angle - Math.PI / 6), pt.y - headlen * Math.sin(angle - Math.PI / 6));
          ctx.moveTo(pt.x, pt.y);
          ctx.lineTo(pt.x - headlen * Math.cos(angle + Math.PI / 6), pt.y - headlen * Math.sin(angle + Math.PI / 6));
          ctx.stroke();
        } else if (tool === "circle") {
          // Circle preview
          const width = pt.x - lastPoint.current.x;
          const height = pt.y - lastPoint.current.y;
          const radiusX = Math.abs(width) / 2;
          const radiusY = Math.abs(height) / 2;
          const centerX = lastPoint.current.x + width / 2;
          const centerY = lastPoint.current.y + height / 2;

          ctx.beginPath();
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
        } else if (tool === "rectangle") {
          // Rectangle preview
          const width = pt.x - lastPoint.current.x;
          const height = pt.y - lastPoint.current.y;

          ctx.beginPath();
          ctx.rect(lastPoint.current.x, lastPoint.current.y, width, height);
          ctx.stroke();
        }
      }
    },
    [getPointFromEvent, activeTool, getAllPoints, drawPointsOnCanvas],
  );

  const doStopDrawing = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;

    const tool = activeTool;

    if (tool === "arrow" || tool === "circle" || tool === "rectangle") {
      // For shapes, save the single shape annotation
      if (lastPoint.current && endPoint.current) {
        const point: AnnotationPoint = {
          x: lastPoint.current.x,
          y: lastPoint.current.y,
          ss: brushColorRef.current,
          lw: lineWidthRef.current,
          ex: endPoint.current.x,
          ey: endPoint.current.y,
          px: scaleRef.current,
          type: tool,
          width: endPoint.current.x - lastPoint.current.x,
          height: endPoint.current.y - lastPoint.current.y,
        };
        addStrokeRef.current([point]);
      }
    } else if (currentStroke.current.length > 0) {
      // For pen/highlighter strokes
      addStrokeRef.current([...currentStroke.current]);
    }

    currentStroke.current = [];
    lastPoint.current = null;
    endPoint.current = null;
  }, [activeTool]);

  // ── Touch handlers (native addEventListener with { passive: false }) ──

  useEffect(() => {
    const canvas = annotationCanvasRef.current;
    if (!canvas) return;

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      doStartDrawing(t.clientX, t.clientY);
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      doDraw(t.clientX, t.clientY);
    };
    const onTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      doStopDrawing();
    };

    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
    };
  }, [doStartDrawing, doDraw, doStopDrawing]);

  // ── Page / scale / rotation change — all imperative ────────────────

  const handlePageChange = useCallback(
    async (page: number) => {
      // Save current position
      try {
        await DvsEvaluationService.savePaperPosition(
          mappingId,
          currentPage,
          rotation,
          scale,
        );
      } catch {}

      // 1. Render PDF page → get dims
      const dims = await renderPage(page);

      // 2. Set annotation canvas dims (clears canvas — that's fine)
      setAnnotationCanvasDims(dims);

      // 3. Load annotations from DB (awaits pending saves)
      const points = await loadPage(mappingId, page);

      // 4. Check for saved position
      let drawScale = scale;
      try {
        const pos = await DvsEvaluationService.getPaperPosition(
          mappingId,
          page,
        );
        if (pos.angle !== rotation || pos.scale !== scale) {
          const newDims = await renderPage(page, pos.scale, pos.angle);
          setAnnotationCanvasDims(newDims);
          setScaleState(pos.scale);
          setRotationState(pos.angle);
          drawScale = pos.scale;
        }
      } catch {}

      // 5. Draw annotations — direct, imperative, no effects
      drawPointsOnCanvas(points, drawScale);
    },
    [
      mappingId,
      currentPage,
      rotation,
      scale,
      renderPage,
      loadPage,
      drawPointsOnCanvas,
      setAnnotationCanvasDims,
      setScaleState,
      setRotationState,
    ],
  );

  const handleScaleChange = useCallback(
    async (newScale: number) => {
      const dims = await setScale(newScale);
      setAnnotationCanvasDims(dims);
      drawPointsOnCanvas(getAllPoints(), newScale);

      try {
        await DvsEvaluationService.savePaperPosition(
          mappingId,
          currentPage,
          rotation,
          newScale,
        );
      } catch {}
    },
    [
      mappingId,
      currentPage,
      rotation,
      setScale,
      drawPointsOnCanvas,
      getAllPoints,
      setAnnotationCanvasDims,
    ],
  );

  const handleRotationChange = useCallback(
    async (newRotation: number) => {
      const dims = await setRotation(newRotation);
      setAnnotationCanvasDims(dims);
      drawPointsOnCanvas(getAllPoints(), scale);

      try {
        await DvsEvaluationService.savePaperPosition(
          mappingId,
          currentPage,
          newRotation,
          scale,
        );
      } catch {}
    },
    [
      mappingId,
      currentPage,
      scale,
      setRotation,
      drawPointsOnCanvas,
      getAllPoints,
      setAnnotationCanvasDims,
    ],
  );

  const handleToolChange = useCallback((tool: AnnotationTool) => {
    setActiveTool(tool);
  }, []);

  // Text box handlers - with local state for editing
  const handleStartEditingTextBox = useCallback((id: string) => {
    const allPoints = getAllPoints();
    const textBox = allPoints.find((p) => p.type === "text" && p.id === id);
    if (textBox) {
      setEditingTextBox(id);
      setEditingTextContent(textBox.text || "");
    }
  }, [getAllPoints]);

  const handleSaveTextBox = useCallback((id: string, newText: string) => {
    // Update the annotation in the annotation store
    const allPoints = getAllPoints();
    const updatedPoints = allPoints.map((p) => {
      if (p.type === "text" && p.id === id) {
        return { ...p, text: newText, ss: brushColor, fontSize: lineWidth + 14 };
      }
      return p;
    });
    // Clear and re-add all points to trigger save
    clearPage();
    addStroke(updatedPoints);
    setEditingTextBox(null);
    setEditingTextContent("");
  }, [getAllPoints, clearPage, addStroke, brushColor, lineWidth]);

  const handleTextBoxDelete = useCallback((id: string) => {
    // Remove from annotations
    const allPoints = getAllPoints();
    const filteredPoints = allPoints.filter((p) => !(p.type === "text" && p.id === id));
    clearPage();
    addStroke(filteredPoints);
    if (editingTextBox === id) {
      setEditingTextBox(null);
      setEditingTextContent("");
    }
  }, [getAllPoints, clearPage, addStroke, editingTextBox]);

  // ── Mouse handlers (React props — passive is fine for mouse) ───────

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      // Close any open text box when clicking on canvas
      if (editingTextBox && editingTextContent !== null) {
        handleSaveTextBox(editingTextBox, editingTextContent);
      }

      if (activeTool === "text") {
        // For text tool, create a new text box annotation
        const pt = getPointFromEvent(e.clientX, e.clientY);
        const newId = `text_${Date.now()}`;
        const textAnnotation: AnnotationPoint = {
          x: pt.x,
          y: pt.y,
          ss: brushColor,
          lw: lineWidth,
          ex: pt.x,
          ey: pt.y,
          px: scale,
          type: "text",
          text: "",
          fontSize: 16,
          width: 200,
          height: 60,
          id: newId,
        };
        // Add to annotations
        addStroke([textAnnotation]);
        // Set editing state immediately for new text box
        setEditingTextBox(newId);
        setEditingTextContent("");
        // Switch back to pen tool after creating text box
        setActiveTool("pen");
      } else {
        doStartDrawing(e.clientX, e.clientY);
      }
    },
    [doStartDrawing, activeTool, getPointFromEvent, brushColor, lineWidth, scale, addStroke, editingTextBox, editingTextContent, handleSaveTextBox],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Track cursor position for text tool tooltip
      if (activeTool === "text") {
        const canvas = annotationCanvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          setCursorPosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          });
        }
      } else {
        setCursorPosition(null);
      }

      doDraw(e.clientX, e.clientY);
    },
    [doDraw, activeTool],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <AnnotationToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        scale={scale}
        rotation={rotation}
        flipped={flipped}
        brushColor={brushColor}
        lineWidth={lineWidth}
        canUndo={canUndo}
        canRedo={canRedo}
        activeTool={activeTool}
        onPageChange={handlePageChange}
        onScaleChange={handleScaleChange}
        onRotationChange={handleRotationChange}
        onFlip={() => setFlipped((f) => !f)}
        onColorChange={setBrushColor}
        onLineWidthChange={setLineWidth}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onClear={handleClear}
        onToolChange={handleToolChange}
      />

      <div
        style={{
          flex: 1,
          overflow: "auto",
          background: "var(--surface-sunken)",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "10px",
          position: "relative",
        }}
      >
        {pdfLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--surface-sunken)",
              zIndex: 10,
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid var(--border-strong)",
                borderTopColor: "#337ab7",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <div style={{ color: "var(--text-muted)", fontSize: "0.95em" }}>
              Loading PDF...
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        <div
          style={{
            position: "relative",
            display: "inline-block",
            transform: flipped ? "scaleX(-1)" : undefined,
          }}
        >
          <canvas ref={pdfCanvasRef} style={{ display: "block", position: "relative", zIndex: 1 }} />
          <canvas
            ref={annotationCanvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: canvasDims.width > 0 ? `${canvasDims.width}px` : "100%",
              height: canvasDims.height > 0 ? `${canvasDims.height}px` : "100%",
              cursor: "crosshair",
              zIndex: 2,
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={doStopDrawing}
            onMouseLeave={doStopDrawing}
          />
          {/* Text boxes layer - render from annotations */}
          {getTextBoxesFromAnnotations().map((textBox) => {
            const isEditing = editingTextBox === textBox.id;
            const isHovering = hoverTextBoxId === textBox.id;

            return (
              <div
                key={textBox.id}
                style={{
                  position: "absolute",
                  left: `${textBox.x}px`,
                  top: `${textBox.y}px`,
                  width: isEditing ? `${textBox.width}px` : "auto",
                  minHeight: isEditing ? `${textBox.height}px` : "auto",
                  maxWidth: isEditing ? `${textBox.width}px` : "300px",
                  border: isEditing ? "2px solid #337ab7" : `1px solid ${textBox.color}`,
                  background: isEditing ? "#ffffff" : "transparent",
                  boxShadow: isEditing ? "0 2px 8px rgba(0,0,0,0.2)" : (isHovering ? "0 1px 4px rgba(0,0,0,0.15)" : "none"),
                  zIndex: isEditing ? 20 : 10,
                  cursor: isEditing ? "text" : "pointer",
                  padding: isEditing ? "8px" : "2px 4px",
                  boxSizing: "border-box",
                  borderRadius: isEditing ? "4px" : "2px",
                }}
                onMouseEnter={() => setHoverTextBoxId(textBox.id)}
                onMouseLeave={() => setHoverTextBoxId(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isEditing) {
                    handleStartEditingTextBox(textBox.id);
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Hover icons - edit and delete */}
                {!isEditing && isHovering && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-10px",
                      right: "-10px",
                      display: "flex",
                      gap: "4px",
                    }}
                  >
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEditingTextBox(textBox.id);
                      }}
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: "#337ab7",
                        color: "var(--text-on-brand)",
                        border: "2px solid #fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: "bold",
                        padding: 0,
                        lineHeight: 1,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      }}
                      title="Edit text"
                    >
                      ✎
                    </button>
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTextBoxDelete(textBox.id);
                      }}
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: "var(--status-danger)",
                        color: "var(--text-on-brand)",
                        border: "2px solid #fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                        padding: 0,
                        lineHeight: 1,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      }}
                      title="Delete text box"
                    >
                      ×
                    </button>
                  </div>
                )}

                {isEditing ? (
                  <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <textarea
                      autoFocus
                      value={editingTextContent}
                      onChange={(e) => setEditingTextContent(e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        minHeight: "50px",
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        resize: "vertical",
                        fontFamily: "Arial, sans-serif",
                        fontSize: `${lineWidth + 14}px`,
                        color: brushColor,
                        padding: 0,
                      }}
                      placeholder="Type your comment here..."
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      fontFamily: "Arial, sans-serif",
                      fontSize: `${textBox.fontSize}px`,
                      color: textBox.color,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.2,
                    }}
                  >
                    {textBox.text || ""}
                  </div>
                )}
              </div>
            );
          })}

          {/* Tooltip for text tool */}
          {activeTool === "text" && cursorPosition && (
            <div
              style={{
                position: "fixed",
                left: `${cursorPosition.x + 15}px`,
                top: `${cursorPosition.y + 15}px`,
                background: "rgba(0,0,0,0.75)",
                color: "var(--text-on-brand)",
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                pointerEvents: "none",
                zIndex: 1000,
                whiteSpace: "nowrap",
              }}
            >
              Click to place text box here
            </div>
          )}

          {rendering && !pdfLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.5)",
              }}
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  border: "3px solid var(--border-strong)",
                  borderTopColor: "#337ab7",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfAnnotationViewer;
